import { NavLink, useLocation } from "react-router-dom";
import { Box, Divider, Typography } from "@mui/joy";
import { motion, AnimatePresence } from "framer-motion";

import {
    FileStack,
    Layers,
    LayoutDashboard,
    ShoppingBasket,
    Store,
    ShieldHalf,
    Logs,
    Fuel,
    BarChartBig,
    Bell,
    ArrowLeft,
    Plus,
    CalendarRange,
    Settings2,
    Tag,
} from "lucide-react";

import { IconButton } from "../../shared";

import "./index.scss";
import { useProject } from "../../hooks/query";
import { useEffect, useState } from "react";
import { IProjectList } from "../../interfaces";
import { usePastelColors } from "../../hooks";
import { Link } from "react-router-dom";

export default function NavigationPanel() {
    const pathname = useLocation();
    const isAdminRoute = pathname.pathname.startsWith("/admin");

    const panelVariants = {
        initial: { x: "-40px", opacity: 0 },
        animate: { x: 0, opacity: 1 },
        exit: { x: "40px", opacity: 0 },
    };

    return (
        <Box className="NavigationWrapper" sx={{ height: "100%" }}>
            <Box sx={{ p: 2, pb: 0 }}>
                <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography level="h4" sx={{ fontFamily: "Merriweather" }}>
                        RoofinMotion
                    </Typography>
                </Box>
                <Typography level="body-xs" sx={{ marginTop: -0.5, fontSize: 13 }}>
                    Growth through efficiency
                </Typography>
            </Box>

            <AnimatePresence mode="wait">
                <motion.div
                    key={String(isAdminRoute)}
                    variants={panelVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={{
                        type: "tween",
                        duration: 0.2,
                        ease: "easeInOut",
                    }}
                >
                    {isAdminRoute ? <NavigationPanelAdmin /> : <NavigationPanelMenu />}
                </motion.div>
            </AnimatePresence>
        </Box>
    );
}

function NavigationPanelAdmin() {
    return (
        <Box>
            <Box mt={3} px={2} pb={2} display={"flex"} flexDirection={"column"} gap={0.5}>
                <Link to={"/"}>
                    <Box display={"flex"} alignItems={"center"} ml={0.5} mb={2} gap={0.5} color={"primary"}>
                        <ArrowLeft size={16} color="currentColor" />
                        <Typography typography={"body-sm"} sx={{ fontWeight: 600, color: "primary" }}>
                            Home
                        </Typography>
                    </Box>
                </Link>
                {adminMenus.map((e: any) => {
                    return (
                        <Box>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    ml: 1,
                                    mt: 1,
                                    textTransform: "uppercase",
                                    color: "text.primary",
                                }}
                                level="body-xs"
                                gutterBottom
                            >
                                {e.category}
                            </Typography>
                            {e.menu.map((menuItem: any) => (
                                <NavigationItem {...menuItem} key={menuItem.name} />
                            ))}

                            <Divider sx={{ mt: 2, mb: 1 }} />
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function NavigationPanelMenu() {
    const { useList } = useProject();
    const projectListSvc = useList();

    const [projects, setProjects] = useState<IProjectList[]>([]);

    const fetchData = async () => {
        try {
            const response = await projectListSvc.mutateAsync();
            setProjects(response);
        } catch (e) {
            console.error("Error fetching data", e);
        }
    };

    useEffect(() => {
        fetchData().then();
    }, []);

    return (
        <Box>
            <Box mt={6} px={2} pb={2} display={"flex"} flexDirection={"column"} gap={0.5}>
                {menus.map((e: any) => {
                    return <NavigationItem {...e} key={e.name} />;
                })}
            </Box>
            <Divider sx={{ mt: 2 }} />

            <Box px={2.5} pt={3} display={"flex"} justifyContent={"space-between"} alignItems={"center"} pb={1}>
                <Typography sx={{ color: "text.primary", fontWeight: 500 }} level="body-sm">
                    Projects
                </Typography>
                <IconButton
                    color={"neutral"}
                    sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 8,
                        "--IconButton-size": "22px",
                    }}
                >
                    <Plus size={"14px"} color="currentColor" />
                </IconButton>
            </Box>
            <Box px={2} pt={0} display={"flex"} flexDirection={"column"} gap={1} flex={1} overflow={"auto"}>
                {projects.slice(0, 10).map((project: any) => {
                    return <ProjectItems {...project} key={project.Id} />;
                })}
            </Box>
        </Box>
    );
}

function ProjectItems(props: any) {
    const { Color, Name, Id }: any = { ...props };
    const pasterColors = usePastelColors(Id);
    const color = Color ? Color : pasterColors;

    return (
        <Link to={`/project/list/${Id}`}>
            <Box display={"flex"} alignItems={"center"} gap={2} py={1} ml={1} mr={2}>
                <Box sx={{ background: color, height: 10, width: 10, borderRadius: 3 }}></Box>
                <Typography
                    level={"body-sm"}
                    sx={{
                        color: "inherit",
                        fontWeight: 500,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {Name}
                </Typography>
            </Box>
        </Link>
    );
}
function NavigationItem(props: any) {
    const { name, href, icon: Icon }: any = { ...props };
    const pathname = useLocation();

    // If the href is '/' (root route), handle it separately
    const isRoot = href === "/";
    const activeLink = isRoot ? pathname === href : pathname.pathname.startsWith(href);

    return (
        <NavLink to={href} className={`navigation-item ${activeLink ? "active" : ""}`}>
            <Box display={"flex"} alignItems={"center"} gap={1} sx={activeLink ? { color: "white" } : {}}>
                <Icon size={16} />
                <Typography sx={{ color: "inherit", fontWeight: 500 }} level="body-sm">
                    {name}
                </Typography>
            </Box>
        </NavLink>
    );
}

const menus = [
    {
        name: "Dashboard",
        href: "/dashboard",
        icon: LayoutDashboard,
    },
    {
        name: "Projects",
        href: "/project",
        icon: Layers,
    },
    {
        name: "Reports",
        href: "/reports/list",
        icon: FileStack,
    },
    // {
    //     name: "Inventory",
    //     href: "/inventory/list",
    //     icon: ShoppingBasket,
    // },
    {
        name: "Suppliers",
        href: "/suppliers",
        icon: Store,
    },
    {
        name: "Calendar",
        href: "/calendar",
        icon: CalendarRange,
    },
];

const adminMenus = [
    {
        category: "Platform",
        menu: [
            {
                name: "Manufacturers",
                href: "/admin/manufacture",
                icon: Layers,
            },
            {
                name: "Product Categories",
                href: "/admin/product-category",
                icon: Tag,
            },
            {
                name: "General",
                href: "/admin/general",
                icon: Settings2,
            },
        ],
    },
    {
        category: "User Management",
        menu: [
            {
                name: "Users",
                href: "/admin/users",
                icon: FileStack,
            },
            {
                name: "Roles",
                href: "/admin/roles",
                icon: ShieldHalf,
            },
        ],
    },
    {
        category: "Advanced",
        menu: [
            {
                name: "Audit Logs",
                href: "/admin/audit-logs",
                icon: Logs,
            },
            {
                name: "Billing",
                href: "/admin/billing",
                icon: Fuel,
            },
            {
                name: "Notifications",
                href: "/admin/billing",
                icon: Bell,
            },
        ],
    },
    {
        category: "Reports",
        menu: [
            {
                name: "Analytics",
                href: "/admin/reports",
                icon: BarChartBig,
            },
        ],
    },
];
